import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';

import PendingHelloSignForms from '../CandidatesList/QuickProfileCandidate/PendingHellosignForms';
import FileViewer from './FileViewer';

import { DocumentIcon, DownloadIconGrey, UploadIconGrey } from 'ui-kit/icons';
import { ReactComponent as VideoIcon } from 'assets/icon-video.svg';

import { questionType } from 'utils/enums';
import auth from 'utils/auth';
import { QuickProfileUser } from 'types';
import { useHistory } from 'react-router-dom';
import { nimbleTheme } from 'theme';
import { HeaderLabel } from './styles';
import { DefaultContainer } from 'features/PrintView/styles';
import axios from 'axios';
import { useState } from 'react';
import { DeletionModal } from 'sharedComponents/Modal/DeletionModal';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { ATSAttachmentsDataTestIds } from 'data-testids/ATS';

interface Props {
  fullUser: QuickProfileUser;
  districtApplicationBeingQuickviewed;
  openDocumentUploadModal?: (districtApplicationId: number) => void;
  openHellosignTemplateModal?: () => void;
  helloSignTemplateModalOpen?: boolean;
  showDownloadIcon: boolean;
  isTalentMarketplace?: boolean;
  isCandidateList?: boolean;
  handleDelete?: (id: number) => void | (() => void);
}

interface AttachmentsHeaderContainerProps {
  countIconsShowing: number;
}

export default function Attachments({
  fullUser,
  districtApplicationBeingQuickviewed,
  openDocumentUploadModal,
  openHellosignTemplateModal,
  helloSignTemplateModalOpen,
  showDownloadIcon,
  isCandidateList,
  isTalentMarketplace,
  handleDelete,
}: Props): JSX.Element {
  const [openedModalId, setOpenedModalId] = useState<number | null>(null);
  const [isDeleteError, setIsDeleteError] = useState<boolean>(false);

  const history = useHistory();
  const user = auth.getUser();

  const handleDeleteClick = (id: number) => {
    setOpenedModalId(id);
  };

  const handleModalDelete = async (): Promise<void> => {
    setIsDeleteError(false);

    try {
      const response = await axios.delete(`/api/application-attachments/${openedModalId}`);

      if (response.status >= 400) {
        throw new Error('Delete failed with status ' + response.status);
      }

      handleDelete(openedModalId);
      setOpenedModalId(null);
    } catch (e) {
      console.error(e);
      setIsDeleteError(true);
    }
  };

  let attachmentAnswers = [];
  let videoLinkAnswers = [];

  // Combine & sort attachment & video link questions
  if (districtApplicationBeingQuickviewed) {
    attachmentAnswers = districtApplicationBeingQuickviewed.applicationattachment_set || [];

    attachmentAnswers = attachmentAnswers.map((answer) => {
      let question;
      if (answer.required_application_attachment) {
        question = districtApplicationBeingQuickviewed.role.requiredapplicationattachment_set.find(
          (question) => answer.required_application_attachment === question.id
        );
      }

      // Addtional documents don't have an associated question and therefore don't have order
      if (!question) {
        question = { order: -1 };
      }

      return { ...answer, question };
    });

    videoLinkAnswers = districtApplicationBeingQuickviewed.applicationattachmentvideourl_set || [];
    videoLinkAnswers = videoLinkAnswers.map((answer) => {
      const question = answer.required_application_attachment;
      return { ...answer, question };
    });
  }

  const attachmentAndVideoLinkAnswers = [...attachmentAnswers, ...videoLinkAnswers].sort(
    (a, b) => a.question.order - b.question.order
  );

  let countIconsShowing = 0;
  let showFormSendIcon = false;
  let renderDownloadIcon = false;
  let renderUploadIcon = false;
  if (auth.isDistrictAdmin()) {
    showFormSendIcon = true;
    countIconsShowing += 1;
  }
  if (showDownloadIcon) {
    renderDownloadIcon = true;
    countIconsShowing += 1;
  }
  if (auth.isDistrictUser()) {
    renderUploadIcon = true;
    countIconsShowing += 1;
  }

  return (
    <>
      {isCandidateList && (
        <BoxContainer data-testid={ATSAttachmentsDataTestIds.CONTAINER}>
          <AttachmentsHeaderContainer countIconsShowing={countIconsShowing}>
            <HeaderLabel className="printViewHeader" data-testid={ATSAttachmentsDataTestIds.HEADER}>
              Attachments
            </HeaderLabel>
            {auth.hasOnboardingModule() && showFormSendIcon && (
              <DocumentIcon
                className="pointer"
                data-tip
                data-testid={ATSAttachmentsDataTestIds.SEND_FORM_ICON}
                data-for="form"
                data-place="bottom"
                data-delay-show="100"
                onClick={openHellosignTemplateModal}
              />
            )}
            <ReactTooltip id="form" effect="solid">
              <span
                data-testid={ATSAttachmentsDataTestIds.SEND_FORM_TOOLTIP_SPAN}
                style={{ color: 'white' }}
              >
                Send a form
              </span>
            </ReactTooltip>
            {renderDownloadIcon && (
              <DownloadIconGrey
                className="pointer"
                onClick={() =>
                  window.open(
                    `/api/attachments/${districtApplicationBeingQuickviewed.id}?uid=${fullUser.id}`,
                    '_blank'
                  )
                }
                data-tip
                data-for="download"
                data-place="bottom"
                data-delay-show="100"
              />
            )}
            <ReactTooltip id="download" effect="solid">
              <span style={{ color: 'white' }}>Download files</span>
            </ReactTooltip>
            {renderUploadIcon && (
              <UploadIconGrey
                className="pointer"
                data-tip
                data-for="upload"
                data-place="bottom"
                data-delay-show="100"
                onClick={() => openDocumentUploadModal(districtApplicationBeingQuickviewed.id)}
              />
            )}
            <ReactTooltip id="upload" effect="solid">
              <span style={{ color: 'white' }}>Upload file</span>
            </ReactTooltip>
          </AttachmentsHeaderContainer>

          {fullUser.profile.resume && (
            <FileViewer file={fullUser.profile.resume} title={'Resume'} />
          )}
          {districtApplicationBeingQuickviewed &&
            attachmentAndVideoLinkAnswers.map((answer, index) => {
              if (answer.question && answer.question.question_type === questionType.videoLink) {
                return (
                  <FileViewer
                    key={index}
                    id={answer.id}
                    file={answer.video_url}
                    title={answer.question.title}
                    icon={<VideoIcon />}
                  />
                );
              } else if (
                answer.question &&
                answer.question.question_type === questionType.attachment
              ) {
                return (
                  <FileViewer
                    key={index}
                    file={answer.attachment}
                    title={answer.question && answer.question.title}
                    id={answer.id}
                  />
                );
              } else {
                // Additional uploaded documents
                return (
                  <FileViewer
                    key={answer.id}
                    id={answer.id}
                    file={answer.attachment}
                    title={answer.title}
                    createdById={answer.created_by_id}
                    showDelete={true}
                    user={user}
                    onDeleteClick={handleDeleteClick}
                  />
                );
              }
            })}
          {fullUser.attachments &&
            fullUser.attachments.map((file) => {
              return file.attachment ? (
                <FileViewer key={file.id} file={file.attachment} title={file.title} />
              ) : (
                ''
              );
            })}
          {districtApplicationBeingQuickviewed?.id && (
            <DefaultContainer>
              <PendingHelloSignForms
                helloSignTemplateModalOpen={helloSignTemplateModalOpen}
                application={districtApplicationBeingQuickviewed}
              />
            </DefaultContainer>
          )}
        </BoxContainer>
      )}
      {isTalentMarketplace && (
        <MarketplaceCredentialContainer>
          <MarketplaceAttachmentsHeaderContainer>
            <MarketplaceHeaderLabel>Attachments</MarketplaceHeaderLabel>
            {renderDownloadIcon && (
              <DownloadIconContainer>
                <DownloadIconGrey
                  className="pointer"
                  onClick={() =>
                    history.push(
                      `/api/attachments/${districtApplicationBeingQuickviewed.id}?uid=${fullUser.id}`,
                      '_blank'
                    )
                  }
                  data-tip
                  data-for="download"
                  data-place="bottom"
                  data-delay-show="100"
                />
              </DownloadIconContainer>
            )}
            <ReactTooltip id="download" effect="solid">
              <span style={{ color: 'white' }}>Download files</span>
            </ReactTooltip>
          </MarketplaceAttachmentsHeaderContainer>
          {fullUser.profile.resume && (
            <FileViewerContainer>
              <FileViewer file={fullUser.profile.resume} title={'Resume'} />
            </FileViewerContainer>
          )}
          {fullUser.attachments &&
            fullUser.attachments.map((file) =>
              file.attachment ? (
                <FileViewerContainer key={file.id}>
                  <FileViewer key={file.id} file={file.attachment} title={file.title} />
                </FileViewerContainer>
              ) : (
                ''
              )
            )}
        </MarketplaceCredentialContainer>
      )}
      <DeletionModal
        onClose={() => {
          setOpenedModalId(null);
          setIsDeleteError(false);
        }}
        onDelete={handleModalDelete}
        open={openedModalId !== null}
        text="You will not be able to recover this attachment once it is deleted."
        title="Are you sure?"
      />
      <ErrorSnackbar
        message="Something went wrong"
        onClose={() => setIsDeleteError(false)}
        open={isDeleteError}
        duration={6000}
        dataTestId={ATSAttachmentsDataTestIds.DELETE_ERROR_SNACKBAR}
      />
    </>
  );
}

const AttachmentsHeaderContainer = styled.div<AttachmentsHeaderContainerProps>`
  display: grid;
  grid-template-columns: 1fr repeat(${({ countIconsShowing }) => countIconsShowing}, 20px);
  grid-gap: 20px;
  align-items: center;

  margin-bottom: 1rem;

  @media print {
    margin-bottom: 0;
  }
`;

export const MarketplaceAttachmentsHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const DownloadIconContainer = styled.div`
  display: flex;
  align-self: end;
  justify-self: end;
  justify-content: end;
  justify-items: end;
  width: 100%;
  padding-right: 35px;
  margin-bottom: 16px;
`;

export const UploadIconContainer = styled.div`
  display: flex;
  align-self: end;
  justify-self: end;
  justify-content: end;
  justify-items: end;
  width: 100%;
  margin-bottom: 16px;
  padding-right: 35px;
`;

export const FileViewerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  padding-right: 35px;
  margin-bottom: 1rem;
`;

export const MarketplaceHeaderLabel = styled.div`
  color: #4F4F4F;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  display; flex;
  align-items: center;
  order: 0;
  flex-grow: 0;
  margin-bottom: 8px;
`;

export const MarketplaceCredentialContainer = styled.div`
  box-sizing: border-box;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  width: 95%;
  padding-top: 16px;
  padding-left: 19px;
`;

export const BoxContainer = styled.div`
  padding: ${nimbleTheme.spacing(2)};
  border: 1px solid ${nimbleTheme.palette.white.darker};
  border-radius: ${nimbleTheme.shape.borderRadius * 0.5};

  @media print {
    border: none;
    padding: 0;
  }
`;
